/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IAdminSourceData } from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import Chip from '@design-system/chip';

interface IProps {
  source: IAdminSourceData;
  isSelected: boolean;
  handleClick: () => void;
  handleDelete?: () => void;
  isDeleting?: boolean;
}

const Wrapper = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${(p) => (p.$selected ? color.BROWN_1 : color.GRAY_3)};
  background-color: ${(p) => (p.$selected ? color.BROWN_4 : color.WHITE_1)};
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    border-color: ${color.BROWN_1};
  }
`;

const TopRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 6px;
  align-items: center;
`;

const DateChips = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 6px;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 10px;
  align-items: flex-start;
`;

const ThumbnailColumn = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const Thumbnail = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 6px;
  background-color: ${color.GRAY_5};
  background-size: cover;
  background-position: center;
  overflow: hidden;
`;

const Info = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${color.RED_3};
  border-radius: 6px;
  background-color: ${color.WHITE_1};
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${color.RED_4};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const ExpressionSourceItem: React.FC<IProps> = ({
  source,
  isSelected,
  handleClick,
  handleDelete = () => undefined,
  isDeleting = false,
}) => {
  const releaseDate = source.expression?.releaseDate ?? null;
  const checkedAt = source.expression?.checkedAt ?? null;
  const checkedLabel = `Checked At: ${checkedAt ? formatDate(checkedAt) : '-'}`;
  const publishedLabel = `Release Date: ${releaseDate ? formatDate(releaseDate) : '-'}`;

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleDelete();
  };

  return (
    <Wrapper $selected={isSelected} onClick={handleClick}>
      <TopRow>
        <ChipGroup>
          <Chip text={source.languageCode} size='tiny' isSelected theme={ETheme.BROWN} />
          <Chip text={source.platform} size='tiny' isSelected theme={ETheme.GRAY} />
          {source.contentType && (
            <Chip text={source.contentType} size='tiny' isSelected theme={ETheme.BLUE} />
          )}
        </ChipGroup>
        <DateChips>
          <Chip
            text={publishedLabel}
            size='tiny'
            isSelected
            theme={releaseDate ? ETheme.GREEN : ETheme.RED}
          />
          <Chip
            text={checkedLabel}
            size='tiny'
            kind='outlined'
            isSelected
            theme={checkedAt ? ETheme.GREEN : ETheme.RED}
          />
        </DateChips>
      </TopRow>
      <Body>
        <ThumbnailColumn>
          <Thumbnail
            style={source.imageLink ? { backgroundImage: `url(${source.imageLink})` } : undefined}
          />
          <Chip
            text={source.downloadedAt ? 'Downloaded' : 'Not Downloaded'}
            size='tiny'
            isSelected
            theme={source.downloadedAt ? ETheme.GREEN : ETheme.RED}
          />
        </ThumbnailColumn>
        <Info>
          <TextBlock
            text={source.title}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: true, multiline: 2 } }}
          />
          <TextBlock
            text={`${source.clipStart}s ~ ${source.clipEnd}s`}
            design={{ text: { size: 'small', color: color.BLACK_5 } }}
          />
          <TextBlock
            text={source.channel}
            design={{ text: { size: 'small', color: color.BLACK_5, oneline: true } }}
          />
        </Info>
        <DeleteButton
          type='button'
          aria-label={`${source.title} 삭제`}
          disabled={isDeleting}
          onClick={handleDeleteClick}>
          <IconBlock
            icon='trash'
            design={{ icon: { size: 'small', color: color.RED_2 }, box: {} }}
          />
        </DeleteButton>
      </Body>
    </Wrapper>
  );
};

export default ExpressionSourceItem;
